import { useTableGeometryCtx, PIP_SIZE_PX } from './useTableGeometry';
import './gameroom.css';

// ── Per-tier Power-Up count pips ───────────────────────────────────────────────────────────
// Three circles ringing the avatar, one per Pup tier, each showing how many cards of that
// color the seat holds in hand (public info: `player.pupTierCounts` — the counts only, never
// the codes). Tactically this is "how much ammunition is this player sitting on".
//
// Size: slightly larger than the dealer chip, and fixed like it is. NOTE the chip RENDERS at
// 28px, not the 24px in its rule — there's no global border-box, so its 2px border sits outside
// the box. These pips are border-box, so 32px is 32px on screen: 4px larger than the chip.
// PIP_SIZE_PX lives in useTableGeometry.js: that module needs it too, to work out how far the
// pips reach above the top-center seat (the ceiling the stage/phase panel clamps to).
//
// Placement is polar around the avatar's center: R is the compass angle (0° = right of the
// circle, 90° = top, 180° = left) and every pip sits at the same distance X from that center.
// X = avatar radius + HALF the pip's radius, so each pip overlaps the avatar's rim by that
// much. Rendered lowest-tier-first (left to right: silver, gold, celestial).
const PIPS = [
  { tier: 'silver', deg: 135, label: 'Silver' },
  { tier: 'gold', deg: 90, label: 'Gold' },
  { tier: 'celestial', deg: 45, label: 'Celestial' },
];

// The pips ride their own zero-size wrapper pinned to the avatar's center (see .pup-tier-pips),
// so each pip's offset is measured from that origin: +x right, +y down.
const px = (n) => `${n.toFixed(2)}px`;

// Circular avatar centered above a seated player's bubble. The hand of cards is
// rendered separately (SeatCards/HoleCards) just to the right, overlapping this
// circle's edge, with a higher z-index so the cards sit on top — as do the tier pips
// below, which ring the circle but stay UNDER those cards (seat stack, highest first:
// nameplate 12, cards 10/9, pips 8, avatar 7).
// `src` is optional — with none we show a default "P" placeholder. `dimmed` fades the avatar
// when the seat isn't actively participating in the hand (folded / sitting out / away).
// `crowned` marks the leaderboard leader with a gold crown resting on the circle.
// `frozen` marks a seat whose player has time frozen: blue ring + a blue stopwatch badge
// top-right (offset so it coexists with the top-center crown).
// `pupCounts` is the seat's public { silver, gold, celestial } Power-Up hand counts, drawn as
// the three tier pips overlapping the circle's upper rim (see PIPS above). They are a SIBLING
// of the circle rather than a child, sharing its anchor point: .profile-pic's own z-index opens
// a stacking context, and out here the two rules alone decide the paint order — pips over the
// avatar, both under the cards.
const ProfilePicture = ({ seat, src, dimmed = false, crowned = false, frozen = false, pupCounts = null }) => {
    const { seatProfileStyle, avatarRadiusPx } = useTableGeometryCtx();
    const positionStyle = seatProfileStyle(seat);
    if (!positionStyle) return null;

    const pipDistancePx = avatarRadiusPx + PIP_SIZE_PX / 4; // avatar radius + half the pip's radius

    return (
      <>
        <div
            className={`profile-pic${dimmed ? ' player-dimmed' : ''}${frozen ? ' profile-pic-frozen' : ''}`}
            style={positionStyle}
            data-seat={seat}
        >
            {crowned && (
                <svg className="profile-crown" viewBox="0 0 32 22" aria-label="leaderboard leader">
                    <path
                        d="M4 7 L10 12.5 L16 4.5 L22 12.5 L28 7 L25.5 17.5 H6.5 Z"
                        fill="#fbbf24" stroke="#b45309" strokeWidth="1.4" strokeLinejoin="round"
                    />
                    <rect x="6" y="17.5" width="20" height="3.2" rx="1.2" fill="#f59e0b" stroke="#b45309" strokeWidth="1" />
                    <circle cx="4" cy="5.6" r="1.8" fill="#fde047" stroke="#b45309" strokeWidth="0.8" />
                    <circle cx="16" cy="3.2" r="1.8" fill="#fde047" stroke="#b45309" strokeWidth="0.8" />
                    <circle cx="28" cy="5.6" r="1.8" fill="#fde047" stroke="#b45309" strokeWidth="0.8" />
                </svg>
            )}
            {frozen && (
                <svg className="profile-stopwatch" viewBox="0 0 24 26" aria-label="time frozen">
                    {/* Top button + stem, then the round body with a tick hand at ~10 o'clock past. */}
                    <rect x="9.5" y="0.8" width="5" height="2.6" rx="1.1" fill="#4ff1fe" stroke="#0b3b56" strokeWidth="1" />
                    <rect x="10.8" y="3.2" width="2.4" height="3.2" fill="#4ff1fe" stroke="#0b3b56" strokeWidth="0.8" />
                    <circle cx="12" cy="16" r="9" fill="#4ff1fe" stroke="#0b3b56" strokeWidth="1.6" />
                    <circle cx="12" cy="16" r="6.2" fill="#0a0f2e" />
                    <line x1="12" y1="16" x2="12" y2="11.4" stroke="#4ff1fe" strokeWidth="1.6" strokeLinecap="round" />
                    <line x1="12" y1="16" x2="15" y2="17.6" stroke="#4ff1fe" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
            )}
            {src
                ? <img src={src} alt="" className="profile-pic-img" />
                : <span className="profile-pic-default">P</span>}
        </div>

        {/* Zero-size wrapper pinned to the SAME anchor the circle is centered on, so its origin
            is the avatar's center and every pip is placed in plain polar px from there. */}
        <div
            className={`pup-tier-pips${dimmed ? ' player-dimmed' : ''}`}
            style={{ bottom: positionStyle.bottom, left: positionStyle.left }}
            data-seat={seat}
        >
            {PIPS.map(({ tier, deg, label }) => {
                const count = pupCounts?.[tier] ?? 0;
                const rad = (deg * Math.PI) / 180;
                return (
                    <div
                        key={tier}
                        className={`pup-tier-pip pup-tier-pip--${tier}${count === 0 ? ' pup-tier-pip--empty' : ''}`}
                        style={{
                            width: px(PIP_SIZE_PX),
                            height: px(PIP_SIZE_PX),
                            left: px(pipDistancePx * Math.cos(rad)),
                            top: px(-pipDistancePx * Math.sin(rad)), // screen y grows downward
                        }}
                        title={`${count} ${label} Power-Up card${count === 1 ? '' : 's'}`}
                    >
                        {count}
                    </div>
                );
            })}
        </div>
      </>
    );
};

export default ProfilePicture;
